import type { CSSProperties } from 'react'

/**
 * La espina de empresa.
 *
 * Multicompany es el problema central: en una lista de 14 proyectos de 5
 * empresas, lo primero que el ojo necesita resolver es *de quien es esto*. Cada
 * empresa recibe un hue fijo al crearse y ese color aparece siempre como una
 * espina de 3px a la izquierda. NUNCA como fondo ni relleno grande: asi los
 * colores semanticos (vencida, por vencer, en curso) quedan libres.
 */

export const DEFAULT_COMPANY_HUE = 210

export interface HueOptions {
  saturation?: number
  lightness?: number
  alpha?: number
}

export function normalizeHue(hue: number | null | undefined): number {
  if (hue === null || hue === undefined || !Number.isFinite(hue)) return DEFAULT_COMPANY_HUE
  return ((Math.round(hue) % 360) + 360) % 360
}

/** `hueToCss(275)` -> `hsl(275 62% 55%)`. */
export function hueToCss(hue: number, options: HueOptions = {}): string {
  const { saturation = 62, lightness = 55, alpha } = options
  const h = normalizeHue(hue)
  const base = `${h} ${saturation}% ${lightness}%`
  return alpha === undefined ? `hsl(${base})` : `hsl(${base} / ${alpha})`
}

/** Variables inline para pasarle el hue de la empresa a un subarbol. */
export function companyHueStyle(hue: number | null | undefined): CSSProperties {
  const h = normalizeHue(hue)
  return {
    ['--company-hue' as string]: String(h),
    ['--company-color' as string]: hueToCss(h),
  } as CSSProperties
}

/**
 * Hue estable derivado del nombre, para cuando todavia no hay `color_hue` del
 * backend. Deterministico: la misma empresa siempre recibe el mismo color.
 */
export function hueFromString(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  return normalizeHue(Math.abs(hash) % 360)
}

/**
 * La luminosidad y la saturación del monograma de empresa, por tema.
 *
 * Existen en TypeScript además de en `tokens.css` por un motivo concreto: son
 * los valores que hacen que las dos letras se lean, y "se lean" es una
 * afirmación medible que merece un test. El CSS no se puede testear; esto sí.
 * Los dos tienen que decir lo mismo, y `company-hue.test.ts` verifica que estos
 * mantengan AA para **los 360 hues posibles**, no para los cuatro del seed.
 *
 * Por qué no un solo valor para los dos temas: la luminosidad de HSL no es
 * perceptual. Con 62% fijo —lo que había— un verde y un violeta al mismo `L`
 * se leen distinto, y sobre papel las cuatro empresas del seed daban entre
 * 1.32:1 y 2.58:1. El QA funcional lo reportó como 7.1.
 */
export const MONOGRAM_TONE = {
  dark: { saturation: 62, lightness: 76 },
  light: { saturation: 60, lightness: 22 },
} as const

/** El fondo del monograma: el mismo hue, tenue, sobre la superficie del tema. */
export const MONOGRAM_BACKGROUND = { saturation: 40, lightness: 50, alpha: 0.14 } as const
